import React from "react";
import styled, { css } from "styled-components";
import { format } from "date-fns";
import DatePicker from "../../components/DatePicker";
import { BalanceLogContext } from "./ProvideBalanceLog";
import {
  brand_orange_deep,
  brand_gradient,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { ModalContext } from "../../providers/ProvideModal";
import { unmodule } from "../../util";

const getGroupType = (groupText) => {
  if (!groupText) return null;
  const lowerText = groupText.toLowerCase();
  if (lowerText.includes("cash in [accepted]")) return "in_accepted";
  if (lowerText.includes("cash out [accepted]")) return "out_accepted";
  if (lowerText.includes("cash in [pending]")) return "in_pending";
  if (lowerText.includes("cash out [pending]")) return "out_pending";
  if (lowerText.includes("cash in [refunded]")) return "in_refunded";
  return null;
};

const isCashIn = (groupType) => {
  return groupType && groupType.startsWith("in_");
};

const BalanceLogDetailModal = ({ data }) => {
  if (!data) {
    return <div>No data available</div>;
  }

  const payment = data.payment || {};
  const groupType = getGroupType(data.group);
  const cashIn = isCashIn(groupType);
  const isWithdrawal = payment.object === "AffiliatorWithdrawal";
  const hasWarningStatus =
    payment.status && !["Selesai", "Menunggu"].includes(payment.status);
  const hasRejectReason =
    payment.status &&
    payment.status.match(/(ditolak|reject|gagal|kedaluwarsa|dibatalkan)/i);

  return (
    <ModalTable
      role="table"
      aria-label="Balance log transaction details"
      aria-describedby="balance-log-detail-description"
    >
      <caption id="balance-log-detail-description" className="sr-only">
        Detailed information about the balance log transaction
      </caption>
      <tbody>
        <tr>
          <th scope="row">Tanggal</th>
          <td>{data.created_at || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Tipe</th>
          <td>
            {data.group ? (
              <StatusBadge $groupType={groupType}>{data.group}</StatusBadge>
            ) : (
              "-"
            )}
          </td>
        </tr>
        <tr>
          <th scope="row">Nominal</th>
          <td>
            {data.price ? (
              <NominalContainer $isCashIn={cashIn}>
                {cashIn ? "+" : "-"} {data.price}
              </NominalContainer>
            ) : (
              "-"
            )}
          </td>
        </tr>
        {isWithdrawal && (
          <tr>
            <th scope="row">Status</th>
            <td>
              {payment.status ? (
                <>
                  <StatusContainer>
                    <StatusText $isWarning={hasWarningStatus}>
                      {payment.status}
                    </StatusText>
                    {hasWarningStatus && (
                      <StatusWarningIcon
                        src={unmodule(
                          require("../../images/icons/warning.svg"),
                        )}
                        alt="Warning"
                        aria-hidden="true"
                      />
                    )}
                  </StatusContainer>
                  {hasRejectReason && payment.reject_reason && (
                    <RejectReasonText>{payment.reject_reason}</RejectReasonText>
                  )}
                </>
              ) : (
                "-"
              )}
            </td>
          </tr>
        )}
        <tr>
          <th scope="row">No. Referensi</th>
          <td>{payment.number || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Ref. User</th>
          <td>{payment.user_name || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Desc</th>
          <td>{payment.desc || "-"}</td>
        </tr>
        {isWithdrawal && payment.payout_url && (
          <tr>
            <th scope="row">Payout Link</th>
            <td>
              <PayoutLink
                href={payment.payout_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open payout link in new tab"
              >
                <img
                  src={unmodule(
                    require("../../images/icons/money-bill-transfer.svg"),
                  )}
                  alt=""
                  aria-hidden="true"
                />
                Buka Link Pembayaran
              </PayoutLink>
            </td>
          </tr>
        )}
      </tbody>
    </ModalTable>
  );
};

const UnnamedComponent = (props) => {
  const { openModal } = React.use(ModalContext);
  const {
    clearable,
    currentPage,
    dateFilterState,
    datePickerParams,
    fetchData,
    fetchable,
    fetching,
    filterCount,
    handleApplyFilter,
    handleClearFilter,
    handlePageChange,
    inputParams,
    resources,
    resourcesMeta,
  } = React.use(BalanceLogContext);
  const [filterShown, setFilterShown] = React.useState(false);
  const [dateFilterShown, setDateFilterShown] = React.useState(false);

  React.useEffect(() => {
    if (fetching) {
      setFilterShown(false);
      setDateFilterShown(false);
    }
  }, [fetching]);

  const toggleFilter = React.useCallback((evt) => {
    evt.stopPropagation();
    setFilterShown((x) => !x);
    setDateFilterShown(false);
  }, []);
  const toggleDateFilter = React.useCallback((evt) => {
    evt.stopPropagation();
    setDateFilterShown((x) => !x);
    setFilterShown(false);
  }, []);
  React.useEffect(() => {
    if (filterShown) {
      document.addEventListener("click", toggleFilter);
      return () => {
        document.removeEventListener("click", toggleFilter);
      };
    }
    if (dateFilterShown) {
      document.addEventListener("click", toggleDateFilter);
      return () => {
        document.removeEventListener("click", toggleDateFilter);
      };
    }
  }, [filterShown, dateFilterShown]);

  const showDetail = React.useCallback(
    (data) => () => {
      const paymentObject = data?.payment?.object || "Unknown";
      openModal({
        title: `Komisi - ${paymentObject}`,
        body: <BalanceLogDetailModal data={data} />,
      });
    },
    [openModal],
  );

  const handleRefresh = React.useCallback(
    (evt) => {
      evt.stopPropagation();
      if (!fetching && fetchData) {
        fetchData();
      }
    },
    [fetchData, fetching],
  );

  return (
    <>
      <TableHeader>
        <FilterDiv onClick={toggleFilter}>
          <FilterTextSpan
            {...(filterCount > 0 && {
              $active: true,
            })}
          >
            Filter ({filterCount})
          </FilterTextSpan>
          <img src={unmodule(require("../../images/icons/angle-down.svg"))} />
          <FilterBox
            onClick={(evt) => evt.stopPropagation()}
            {...(filterShown && {
              $shown: true,
            })}
          >
            <FilterBoxHeader>
              <FilterBoxActionClearButton
                onClick={handleClearFilter}
                {...(!clearable && {
                  disabled: true,
                })}
              >
                Clear All
              </FilterBoxActionClearButton>
              <FilterBoxActionApplyButton
                onClick={handleApplyFilter}
                {...(!fetchable && {
                  disabled: true,
                })}
              >
                Apply Filter
              </FilterBoxActionApplyButton>
            </FilterBoxHeader>
            <FilterBoxHeaderFieldSeparator />
            <FilterBoxFields>
              <FilterBoxField>
                <FilterBoxTitle>STATUS</FilterBoxTitle>
                <FilterLabelContainer>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "in_accepted")}
                    />
                    <div>
                      Kas Masuk
                      <span>Berhasil</span>
                    </div>
                  </FilterCheckboxLabel>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "out_pending")}
                    />
                    <div>
                      Kas Keluar
                      <span>Pending</span>
                    </div>
                  </FilterCheckboxLabel>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "out_accepted")}
                    />
                    <div>
                      Kas Keluar
                      <span>Berhasil</span>
                    </div>
                  </FilterCheckboxLabel>
                </FilterLabelContainer>
              </FilterBoxField>
            </FilterBoxFields>
          </FilterBox>
        </FilterDiv>
        <DateFilterDiv onClick={toggleDateFilter}>
          <img src={unmodule(require("../../images/icons/calendar.svg"))} />
          <span>
            {format(dateFilterState.dateQuery?.created_at?.[0], "dd/MM/yyyy")}
          </span>
          <span>
            {format(dateFilterState.dateQuery?.created_at?.[1], "dd/MM/yyyy")}
          </span>
          <img src={unmodule(require("../../images/icons/angle-down.svg"))} />
          <FilterBox
            onClick={(evt) => evt.stopPropagation()}
            {...(dateFilterShown && {
              $shown: true,
            })}
          >
            <FilterBoxHeader>
              <div />
              <FilterBoxActionApplyButton onClick={handleApplyFilter}>
                Apply Filter
              </FilterBoxActionApplyButton>
            </FilterBoxHeader>
            <FilterBoxHeaderFieldSeparator />
            <FilterBoxFields>
              <FilterBoxField>
                <FilterBoxTitle>TANGGAL</FilterBoxTitle>
                <FilterLabelContainer>
                  <FilterSelectLabel>
                    <span>from</span>
                    <DatePicker {...datePickerParams("created_at", 0)} />
                  </FilterSelectLabel>
                  <FilterSelectLabel>
                    <span>to</span>
                    <DatePicker {...datePickerParams("created_at", 1)} />
                  </FilterSelectLabel>
                </FilterLabelContainer>
              </FilterBoxField>
            </FilterBoxFields>
          </FilterBox>
        </DateFilterDiv>
        <RefreshButton onClick={handleRefresh} disabled={fetching}>
          <span>⟳</span>
          <span>Refresh</span>
        </RefreshButton>
      </TableHeader>
      <TableContainer>
        <DefaultTable>
          <thead>
            <tr>
              <th>#</th>
              <th>Tanggal</th>
              <th>Tipe</th>
              <th>Nominal</th>
              <th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((x, idx) => (
              <ClickableTr key={x.id} onClick={showDetail(x)}>
                <td>
                  {idx + 1 + (resourcesMeta.page - 1) * resourcesMeta.perpage}.
                </td>
                <td>{x.created_at}</td>
                <td>
                  <StatusBadge $groupType={getGroupType(x.group)}>
                    {x.group}
                  </StatusBadge>
                </td>
                <td>
                  <NominalContainer $isCashIn={isCashIn(getGroupType(x.group))}>
                    {isCashIn(getGroupType(x.group)) ? "+" : "-"} {x.price}
                  </NominalContainer>
                </td>
                <td>
                  <div>{x.payment.object}</div>
                </td>
              </ClickableTr>
            ))}
          </tbody>
        </DefaultTable>
      </TableContainer>
      {resourcesMeta.pages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(Math.max(1, currentPage - 5))}
            disabled={currentPage <= 1}
            title="Go back 5 pages"
          >
            &lt;&lt;
          </PaginationButton>
          <PaginationButton
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            title="Go to previous page"
          >
            &lt;
          </PaginationButton>
          {(() => {
            const totalPages = resourcesMeta.pages || 1;
            const current = currentPage || 1;
            const maxVisible = 5;
            let startPage, endPage;

            if (totalPages <= maxVisible) {
              startPage = 1;
              endPage = totalPages;
            } else if (current <= 3) {
              startPage = 1;
              endPage = maxVisible;
            } else if (current >= totalPages - 2) {
              startPage = totalPages - maxVisible + 1;
              endPage = totalPages;
            } else {
              startPage = current - 2;
              endPage = current + 2;
            }

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
              pages.push(i);
            }

            return pages.map((page) => (
              <PaginationPageButton
                key={page}
                onClick={() => handlePageChange(page)}
                $active={page === current}
              >
                {page}
              </PaginationPageButton>
            ));
          })()}
          <PaginationButton
            onClick={() =>
              handlePageChange(
                Math.min(resourcesMeta.pages || 1, currentPage + 1),
              )
            }
            disabled={currentPage >= (resourcesMeta.pages || 1)}
            title="Go to next page"
          >
            &gt;
          </PaginationButton>
          <PaginationButton
            onClick={() =>
              handlePageChange(
                Math.min(resourcesMeta.pages || 1, currentPage + 5),
              )
            }
            disabled={currentPage >= (resourcesMeta.pages || 1)}
            title="Go forward 5 pages"
          >
            &gt;&gt;
          </PaginationButton>
        </PaginationContainer>
      )}
    </>
  );
};
export default UnnamedComponent;

const TableContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow-x: auto;
`;

const DefaultTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  & th {
    background: #fff7ed;
    border-bottom: 1px solid #e5e7eb;
    color: ${brand_orange_deep};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    padding: 14px 18px;
    text-align: left;
    text-transform: uppercase;
  }

  & td {
    padding: 14px 18px;
    font-size: 14px;
    color: #1f2937;
  }

  & tr:not(:last-child) > td {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const ClickableTr = styled.tr`
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover > td {
    background-color: #fff7ed;
  }
`;

const TableHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

const FilterDiv = styled.div`
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  align-items: center;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  padding: 8px 12px;
  position: relative;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  transition: border-color 150ms ease;

  &:hover {
    border-color: ${brand_orange_deep};
  }

  & > img {
    width: 16px;
  }
`;

const DateFilterDiv = styled(FilterDiv)`
  & > img:first-child {
    margin-right: 4px;
  }

  & > span {
    font-size: 13px;
  }
`;

const RefreshButton = styled.button`
  align-items: center;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  gap: 5px;
  justify-content: center;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  transition: border-color 150ms ease;

  &:hover:not(:disabled) {
    border-color: ${brand_orange_deep};
    color: ${brand_orange_deep};
  }

  & > span:first-child {
    font-size: 16px;
    display: inline-block;
    transition: transform 0.3s ease;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:not(:disabled):active > span:first-child {
    transform: rotate(360deg);
  }
`;

const FilterBox = styled.div`
  background-color: #ffffff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  color: #1f2937;
  cursor: default;
  display: none;
  left: 0;
  min-width: 220px;
  position: absolute;
  top: 42px;
  z-index: 20;

  ${(props) =>
    props.$shown &&
    css`
      display: block;
    `};
`;

const FilterBoxTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 10px 0 16px;
`;

const FilterBoxHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
`;

const cssFilterBoxActionButton = css`
  border-radius: 8px;
  cursor: pointer;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 700;
  transition: background-color 0.2s ease;

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const FilterBoxActionClearButton = styled.button`
  ${cssFilterBoxActionButton};
  background-color: transparent;
  border: 1px solid ${brand_orange_deep};
  color: ${brand_orange_deep};

  &:hover:not([disabled]) {
    background-color: #fff7ed;
  }
`;

const FilterBoxActionApplyButton = styled.button`
  ${cssFilterBoxActionButton};
  background: ${brand_gradient};
  border: none;
  color: #ffffff;

  &:hover:not([disabled]) {
    filter: brightness(0.97);
  }
`;

const FilterBoxHeaderFieldSeparator = styled.div`
  background-color: #e5e7eb;
  height: 1px;
`;

const FilterBoxFields = styled.div`
  display: flex;

  @media ${narrowScreenMediaQuery} {
    flex-wrap: wrap;
  }
`;

const FilterBoxField = styled.div`
  padding: 14px;
  border-right: 1px solid #e5e7eb;
  flex: 1 1 0;
`;

const FilterLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FilterCheckboxLabel = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  gap: 10px;
  padding: 6px 14px 6px 6px;
  white-space: nowrap;
  border-radius: 6px;
  transition: background-color 0.15s;

  &:hover {
    background-color: #fff7ed;
    color: ${brand_orange_deep};
  }
`;

const FilterCheckboxInput = styled.input`
  accent-color: ${brand_orange_deep};
  height: 18px;
  width: 18px;
`;

const FilterSelectLabel = styled.div`
  color: #1f2937;
  margin-bottom: 10px;
  min-width: 250px;

  & > span {
    color: #6b7280;
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  & input {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    font-size: 14px;
    letter-spacing: 0.5px;
    line-height: 16px;
    padding: 8px 12px;
    color: #1f2937;
    transition: border-color 150ms ease;

    &:focus {
      outline: none;
      border-color: ${brand_orange_deep};
      box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.12);
    }
  }
`;

const FilterTextSpan = styled.span`
  ${(props) =>
    props.$active &&
    css`
      color: ${brand_orange_deep};
    `};
`;

const PayoutLink = styled.a`
  align-items: center;
  color: ${brand_orange_deep};
  display: inline-flex;
  gap: 8px;
  text-decoration: none;
  font-weight: 700;
  word-break: break-all;

  &:hover {
    color: #f97316;
    text-decoration: underline;
  }

  & > img {
    height: 16px;
    width: 16px;
  }
`;

const StatusContainer = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const StatusText = styled.span`
  ${(props) =>
    props.$isWarning &&
    css`
      color: #dc2626;
      font-weight: bold;
    `};
`;

const StatusWarningIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const RejectReasonText = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(220, 38, 38, 0.06);
  border-left: 3px solid #dc2626;
  border-radius: 3px;
`;

const ModalTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  & caption {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  & th,
  & td {
    padding: 12px 16px;
    vertical-align: top;
    text-align: left;
  }

  & th {
    color: #6b7280;
    font-weight: 600;
    font-size: 13px;
    min-width: 130px;
    white-space: nowrap;
  }

  & td {
    color: #1f2937;
    font-size: 14px;
    word-break: break-word;
  }

  & tr:not(:last-child) > th,
  & tr:not(:last-child) > td {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const NominalContainer = styled.span`
  align-items: center;
  display: inline-flex;
  font-weight: 700;
  gap: 4px;

  ${(props) =>
    props.$isCashIn
      ? css`
          color: #16a34a;
        `
      : css`
          color: #dc2626;
        `}
`;

const StatusBadge = styled.span`
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  justify-content: center;
  padding: 3px 10px;
  white-space: nowrap;

  ${(props) => {
    switch (props.$groupType) {
      case "in_accepted":
        return css`
          background-color: rgba(22, 163, 74, 0.1);
          color: #16a34a;
        `;
      case "out_accepted":
        return css`
          background-color: rgba(42, 159, 214, 0.1);
          color: #2A9FD6;
        `;
      case "in_pending":
      case "out_pending":
        return css`
          background-color: rgba(216, 89, 7, 0.1);
          color: ${brand_orange_deep};
        `;
      case "in_refunded":
        return css`
          background-color: rgba(220, 38, 38, 0.08);
          color: #dc2626;
        `;
      default:
        return css`
          background-color: #f3f4f6;
          color: #6b7280;
        `;
    }
  }}
`;

const PaginationContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 18px;
`;

const PaginationButton = styled.button`
  align-items: center;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  min-width: 36px;
  padding: 8px 12px;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${brand_orange_deep};
    color: ${brand_orange_deep};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const PaginationPageButton = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  min-width: 36px;
  padding: 8px 12px;
  transition: all 0.15s ease;

  background: ${(props) => (props.$active ? brand_gradient : "#ffffff")};
  border: 1px solid ${(props) => (props.$active ? "transparent" : "#e5e7eb")};
  color: ${(props) => (props.$active ? "#ffffff" : "#4b5563")};
  font-weight: ${(props) => (props.$active ? "800" : "normal")};
  box-shadow: ${(props) =>
    props.$active ? "0 4px 12px rgba(216, 89, 7, 0.2)" : "none"};

  &:hover {
    ${(props) =>
      props.$active
        ? css`
            filter: brightness(0.97);
          `
        : css`
            border-color: ${brand_orange_deep};
            color: ${brand_orange_deep};
          `}
  }
`;
